// Cryptographically-random password generator for the admin "New User" forms.
// Produces a password that satisfies the strongest rule shown in the UI —
// min 12 chars with at least one upper, one lower, one number and one symbol —
// so a generated value passes BOTH the caseworker rule (upper/lower/number/symbol)
// and the Zephyr-staff rule (1 uppercase + 1 number). It is a convenience for the
// admin creating the account; the admin still submits the form to create the user.
//
// Ambiguous characters (I, l, 1, O, 0) are omitted so the password can be read
// aloud / retyped without confusion — entropy is still ample at length 16.

const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghijkmnpqrstuvwxyz";
const DIGIT = "23456789";
const SYMBOL = "!@#$%^&*-_=+?";
const ALL = UPPER + LOWER + DIGIT + SYMBOL;

// A uniform random integer in [0, max) using the Web Crypto API (rejection-sampled
// to avoid modulo bias). Available in the browser and in Node's test runner.
function randInt(max: number): number {
  const limit = Math.floor(0xffffffff / max) * max;
  const buf = new Uint32Array(1);
  let n = 0;
  do {
    crypto.getRandomValues(buf);
    n = buf[0];
  } while (n >= limit);
  return n % max;
}

function pick(set: string): string {
  return set[randInt(set.length)];
}

// Generate a compliant random password. Default length 16 (>= the 12 minimum);
// a shorter request is clamped up to 12 so the result can never fail the rule.
export function generatePassword(length = 16): string {
  const len = Math.max(12, Math.floor(length) || 12);
  // Guarantee one of each required class, then fill the remainder from all classes.
  const chars: string[] = [pick(UPPER), pick(LOWER), pick(DIGIT), pick(SYMBOL)];
  for (let i = chars.length; i < len; i++) chars.push(pick(ALL));
  // Fisher-Yates shuffle so the four guaranteed chars aren't always at the front.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randInt(i + 1);
    const tmp = chars[i];
    chars[i] = chars[j];
    chars[j] = tmp;
  }
  return chars.join("");
}
